import "./style.scss";
import * as React from "react";
import * as api from "../../../../apis";
import * as utils from "../../../common/utils";
import _ from "lodash";
import moment from "moment";
import Papa from "papaparse";
import { Button, Modal, Icon, Popover, message } from "antd";
import { ModalContents } from "./ModalContents";
import { Publication } from "../../constants";
import { sendGAEvent, UserContext } from "../../../common/google_analytics";
import {
  generateQueryString,
  parseQueryString
} from "../../../../modules/common/utils";

export interface DataExportProps {
  visible: boolean;
  publications: Publication[];
  startDate: string;
  endDate: string;
  merchantName: string;
  hasFinancePermissions: boolean;
  gaUserContext: UserContext;
}

export interface DataExportActions {
  toggleExportModal: () => void;
}

interface DataExportState {
  loading: boolean;
  report: string;
  versions: any[];
  fetchingVersions: boolean;
  selectedPublication: any;
  selectedVersion: any;
  selectedMetric: string;
}

export interface DataExport {
  contentVersionMetrics: string[];
}

export class DataExport extends React.Component<
  DataExportProps & DataExportActions & RouterChildProps,
  DataExportState
> {
  constructor(props: DataExportProps & DataExportActions & RouterChildProps) {
    super(props);
    this.state = {
      loading: false,
      report: "reach",
      versions: [],
      fetchingVersions: false,
      selectedPublication: null,
      selectedVersion: null,
      selectedMetric: null
    };
    this.contentVersionMetrics = [
      "item_views",
      "clicks",
      "shopping_list",
      "ttm"
    ];
    this.handleExport = _.debounce(this.handleExport.bind(this), 500);
  }

  // Close the modal on unmount, typically for when back button is pressed.
  componentWillUnmount() {
    if (this.props.visible) this.props.toggleExportModal();
  }

  shouldDisableExport = () => {
    if (this.state.report == "content-publication")
      return !this.state.selectedPublication;
    if (this.state.report == "content-version")
      return !this.state.selectedPublication || !this.state.selectedVersion;
  };

  fetchReportData = (type: string) => {
    const { publications, startDate, endDate } = this.props;
    const publicationIds = publications.map(
      (publication: any) => publication.flyer_run_id
    );

    const reportDataApis: { [key: string]: () => Promise<any> } = {
      reach: () => api.fetchReachReport(publicationIds, startDate, endDate),
      audience: () =>
        api.fetchAudienceReport(publicationIds, startDate, endDate),
      finance: () => api.fetchFinanceReport(publicationIds, startDate, endDate),
      "content-publication": () => {
        const publicationId = this.state.selectedPublication.flyer_run_id;
        return api.fetchContentPublicationReport(
          publicationId,
          startDate,
          endDate
        );
      },
      "content-version": () => {
        const versionId = this.state.selectedVersion.flyer_id;
        return api.fetchContentVersionReport(versionId, startDate, endDate);
      }
    };

    return reportDataApis[type]();
  };

  // Redirect to content view to download the version
  handleContentVersionExport = () => {
    const { selectedVersion, selectedPublication, selectedMetric } = this.state;
    const queryProps = parseQueryString(this.props.location.search);
    const newProps = {
      ...queryProps,
      selected_publication_id: selectedPublication.flyer_run_id,
      selected_flyer_id: selectedVersion.flyer_id,
      metric: selectedMetric,
      group_by: "item",
      view: "content",
      contentDownload: true
    };
    this.props.history.push(`?${generateQueryString(newProps)}`);
    location.reload();
  };

  handleExport = () => {
    this.setState({ loading: true });

    // Content Version export is done through the content view
    if (this.state.report == "content-version")
      return this.handleContentVersionExport();

    const reportType = _.startCase(this.state.report);
    this.fetchReportData(this.state.report)
      .then((json: any) => {
        const csvString = Papa.unparse(json);
        const filename = `${this.state.report}-report-${this.props.startDate}-to-${this.props.endDate}`;
        utils.downloadCsv(csvString, filename);

        this.setState({ loading: false });
        this.props.toggleExportModal();

        const { merchantName, gaUserContext } = this.props;
        sendGAEvent(
          "Analytics",
          `Data Export: ${reportType}`,
          merchantName,
          gaUserContext
        );
        message.success(`${reportType} Report has been exported.`, 3);
      })
      .catch((error: any) => {
        console.error(error);
        this.setState({ loading: false });
        let errorText =
          error.response.status == 409
            ? "System is busy."
            : `${reportType} Report has failed to export.`;
        message.error(errorText, 3);
      });
  };

  handleRadioChange = (e: any) => this.setState({ report: e.target.value });

  handlePublicationSelect = (publicationId: number) => {
    if (
      !this.state.selectedPublication ||
      publicationId != this.state.selectedPublication.flyer_run_id
    ) {
      this.setState({
        selectedPublication: this.props.publications.find(
          (pub: any) => pub.flyer_run_id == publicationId
        ),
        selectedVersion: null,
        versions: []
      });

      this.setState({ fetchingVersions: true });
      api.fetchFlyers([publicationId]).then((flyers: any[]) => {
        this.setState({ versions: flyers });
        this.setState({ fetchingVersions: true });
      });
    }
  };

  handleVersionSelect = (versionId: number) =>
    this.setState(prevState => ({
      selectedVersion: prevState.versions.find(
        (version: any) => version.flyer_id == versionId
      )
    }));

  handleMetricSelect = (metric: string) =>
    this.setState({ selectedMetric: metric });

  render() {
    let disableExport =
      moment(this.props.endDate).diff(
        moment(this.props.startDate),
        "months",
        true
      ) >= 4;

    return (
      <div className="data-export">
        <div style={{ paddingRight: 15, pointerEvents: "all" }}>
          <Button
            disabled={disableExport}
            data-automation-id="data-export-button"
            className="export-button"
            type={"primary"}
            onClick={this.props.toggleExportModal}
            style={{ width: "100%" }}
          >
            <div
              style={{
                pointerEvents: "all",
                display: "inline-block",
                fontSize: "13px"
              }}
            >
              <Icon type="download" />
              &nbsp;&nbsp;&nbsp; Export &nbsp;&nbsp;&nbsp;
            </div>
            <Popover
              arrowPointAtCenter
              placement="topLeft"
              title="Help"
              trigger="hover"
              content={<span>Data Export is limited to 4 months of data.</span>}
            >
              <div
                style={{
                  pointerEvents: "all",
                  width: "13px",
                  display: "inline-block"
                }}
              >
                <span
                  className="anticon anticon-exclamation-circle"
                  style={{
                    height: "28px",
                    width: "90px",
                    display: "flex",
                    alignItems: "center"
                  }}
                />
              </div>
            </Popover>
          </Button>
        </div>
        <Modal
          title={<div className="modal-title">Export a Report</div>}
          visible={this.props.visible}
          onCancel={this.props.toggleExportModal}
          width={620}
          footer={[
            <Button
              data-automation-id="data-export-cancel"
              key="cancel"
              onClick={this.props.toggleExportModal}
            >
              Cancel
            </Button>,
            <Button
              data-automation-id="data-export-confirm"
              key="export"
              type="primary"
              loading={this.state.loading}
              disabled={this.shouldDisableExport()}
              onClick={this.handleExport}
            >
              Export
            </Button>
          ]}
        >
          <ModalContents
            {...this.state}
            {...this.props}
            value={this.state.report}
            onRadioChange={this.handleRadioChange}
            metrics={this.contentVersionMetrics}
            handlePublicationSelect={this.handlePublicationSelect}
            handleVersionSelect={this.handleVersionSelect}
            handleMetricSelect={this.handleMetricSelect}
          />
        </Modal>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./client/modules/Analytics/components/DataExport/component.tsx